import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { adminBookingDetailUrl } from "@/constants/adminRoutes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  PENDING: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  PENDING_REVIEW: "bg-red-500/15 text-red-400 border-red-500/30",
  CONFIRMED: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  IN_PROGRESS: "bg-green-500/15 text-green-400 border-green-500/30",
  COMPLETED: "bg-gray-500/15 text-gray-400 border-gray-500/30",
  DECLINED: "bg-red-900/15 text-red-300 border-red-900/30",
  PAID: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
};

const DAYS_OF_WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function AdminCalendar() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/calendar`, { params: { month, year } })
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [month, year]);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
    setSelectedDay(null);
  };

  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
    setSelectedDay(null);
  };

  // Build calendar grid
  const firstDay = new Date(year, month - 1, 1);
  const startDay = (firstDay.getDay() + 6) % 7; // Monday = 0
  const daysInMonth = new Date(year, month, 0).getDate();
  const weeks = [];
  let week = new Array(startDay).fill(null);

  for (let d = 1; d <= daysInMonth; d++) {
    week.push(d);
    if (week.length === 7) { weeks.push(week); week = []; }
  }
  if (week.length > 0) {
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }

  const getDayData = (day) => {
    if (!data?.days || !day) return null;
    const key = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return data.days[key];
  };

  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const selectedDayData = selectedDay ? getDayData(selectedDay) : null;

  return (
    <div className="max-w-5xl mx-auto space-y-5" data-testid="admin-calendar">
      <h1 className="font-outfit text-xl font-bold text-white">Calendar</h1>

      {/* Month Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={prevMonth} className="border-white/10 text-sarathi-text-secondary">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <h2 className="font-outfit text-lg font-semibold text-white">{MONTH_NAMES[month - 1]} {year}</h2>
        <Button variant="outline" size="sm" onClick={nextMonth} className="border-white/10 text-sarathi-text-secondary">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {loading ? (
        <div className="text-sarathi-text-muted text-center py-10">Loading...</div>
      ) : (
        <div className="bg-sarathi-surface border border-white/10 rounded-xl overflow-hidden">
          {/* Day headers */}
          <div className="grid grid-cols-7 border-b border-white/5">
            {DAYS_OF_WEEK.map(d => (
              <div key={d} className="text-center py-2 text-sarathi-text-muted text-xs font-medium">{d}</div>
            ))}
          </div>

          {/* Calendar grid */}
          {weeks.map((week, wi) => (
            <div key={`week-${wi}`} className="grid grid-cols-7 border-b border-white/5 last:border-b-0">
              {week.map((day, di) => {
                if (!day) return <div key={`empty-${wi}-${di}`} className="p-2 min-h-[64px]" />;
                const dayData = getDayData(day);
                const count = dayData?.count || 0;
                const dayKey = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                const isToday = dayKey === todayStr;
                const isSelected = selectedDay === day;

                return (
                  <button
                    type="button"
                    key={dayKey} onClick={() => setSelectedDay(day)}
                    className={`p-2 min-h-[64px] text-left transition-colors hover:bg-white/5 ${isSelected ? "bg-sarathi-primary/10 ring-1 ring-sarathi-primary/30" : ""}`}
                    data-testid={`cal-day-${day}`}
                  >
                    <span className={`text-xs font-medium ${isToday ? "bg-sarathi-primary text-white w-6 h-6 rounded-full inline-flex items-center justify-center" : "text-sarathi-text-secondary"}`}>
                      {day}
                    </span>
                    {count > 0 && (
                      <div className="mt-1">
                        <span className="text-xs bg-sarathi-primary/20 text-sarathi-primary rounded px-1.5 py-0.5">{count}</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Selected Day Bookings */}
      {selectedDay && (
        <div className="bg-sarathi-surface border border-white/10 rounded-xl p-4">
          <h3 className="text-white font-semibold text-sm mb-3">
            {selectedDay} {MONTH_NAMES[month - 1]} {year} — {selectedDayData?.count || 0} booking(s)
          </h3>
          {(!selectedDayData || selectedDayData.bookings.length === 0) ? (
            <p className="text-sarathi-text-muted text-sm py-2">No bookings on this day</p>
          ) : (
            <div className="space-y-2">
              {selectedDayData.bookings.map(b => (
                <Link key={b.id} to={adminBookingDetailUrl(b.id)}
                  className="flex items-center justify-between bg-sarathi-background/50 rounded-lg px-4 py-3 hover:bg-sarathi-background/80 transition-colors">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sarathi-primary font-mono text-xs">{b.time}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_COLORS[b.status] || STATUS_COLORS.PENDING}`}>{b.status}</span>
                    </div>
                    <p className="text-white text-sm truncate">{b.pickup_location} → {b.drop_location}</p>
                    <p className="text-sarathi-text-muted text-xs">{b.passenger_name} · {b.booking_reference}</p>
                  </div>
                  {b.fare_data?.displayTotal && (
                    <span className="text-sarathi-primary font-outfit font-bold text-sm shrink-0 ml-3">{b.fare_data.displayTotal}</span>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
